/**
 * nordura-product-form.js
 * Drives sections/main-product.liquid: variant resolution, subscription vs
 * one-time plan switching, delivery-frequency (selling plan) selection,
 * bulk quantity tiers, gallery sync, and AJAX add-to-cart.
 *
 * No build step, no dependencies - a single custom element so markup and
 * behavior travel together and the section can be duplicated/reordered
 * safely in the Theme Editor.
 */
(function () {
  'use strict';

  if (customElements.get('product-form')) return;

  /** Minimal Shopify money formatter (`shop.money_format` style tokens). */
  function formatMoney(cents, format) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withThousands = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var formatted = withThousands + ',' + parts[1];
    if (!format) return formatted + ' €';
    return format
      .replace(/\{\{\s*amount\s*\}\}/, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withThousands)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted);
  }

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  var ProductForm = function () {
    var el = Object.create(HTMLElement.prototype);
    return el;
  };

  class NorduraProductForm extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}} €';
      this.variants = readJSON(this.querySelector('[data-n-variants-json]')) || [];
      this.sellingPlanGroups = readJSON(this.querySelector('[data-n-selling-plan-groups-json]')) || [];
      this.sizeRecommendations = readJSON(this.querySelector('[data-n-size-recommendations-json]')) || [];
      this.primaryGroup = this.sellingPlanGroups[0] || null;

      this.state = {
        selectedOptions: [],
        plan: this.primaryGroup ? 'subscription' : 'onetime',
        sellingPlanId: this.primaryGroup && this.primaryGroup.selling_plans.length
          ? this.primaryGroup.selling_plans[0].id
          : null,
        qty: 1,
        qtyUnitPriceCents: null
      };

      this.cacheDom();
      this.bootstrapSelectedOptions();
      this.bindGallery();
      this.bindOptionInputs();
      this.bindPlanTabs();
      this.bindFrequency();
      this.bindQuantityTiers();
      this.bindSubmit();

      this.renderFrequencySwatches();
      this.updateAll();
      this.initStickyObserver();
    }

    cacheDom() {
      this.gallery = this.querySelector('[data-n-gallery]');
      this.nativeOptionsWrap = this.querySelector('[data-n-native-options]');
      this.sizeSwatchWrap = this.querySelector('[data-n-size-swatch]');
      this.frequencyWrap = this.querySelector('[data-n-frequency-swatch]');
      this.recommendationEl = this.querySelector('[data-n-recommendation]');
      this.qtyTiersWrap = this.querySelector('[data-n-qty-tiers]');
      this.errorEl = this.querySelector('[data-n-form-error]');
      this.submitBtn = this.querySelector('[data-n-add-to-cart]');
      this.planTabs = Array.prototype.slice.call(this.querySelectorAll('[data-n-plan-tab]'));
    }

    /* ---------------------------------------------------------------- */
    /* Variant resolution                                                */
    /* ---------------------------------------------------------------- */

    bootstrapSelectedOptions() {
      var first = this.variants[0];
      this.state.selectedOptions = first ? first.options.slice() : [];

      // Reflect whatever is pre-selected in the DOM (deep-link / server render)
      var activeSwatch = this.sizeSwatchWrap && this.sizeSwatchWrap.querySelector('.is-active');
      if (activeSwatch && this.sizeSwatchWrap) {
        var pos = parseInt(this.sizeSwatchWrap.dataset.optionPosition, 10) - 1;
        if (pos >= 0) this.state.selectedOptions[pos] = activeSwatch.dataset.value;
      }
    }

    findVariant() {
      var selected = this.state.selectedOptions;
      var match = this.variants.find(function (v) {
        return v.options.every(function (val, i) { return selected[i] === undefined || val === selected[i]; });
      });
      return match || this.variants[0];
    }

    currentVariant() {
      return this._currentVariant || (this._currentVariant = this.findVariant());
    }

    /* ---------------------------------------------------------------- */
    /* Gallery                                                           */
    /* ---------------------------------------------------------------- */

    bindGallery() {
      if (!this.gallery) return;
      this.gallery.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-n-gallery-thumb]');
        if (!thumb) return;
        this.setActiveMedia(thumb.dataset.target);
      }.bind(this));
    }

    setActiveMedia(mediaId) {
      if (!this.gallery) return;
      this.gallery.querySelectorAll('[data-n-gallery-thumb]').forEach(function (t) {
        var active = t.dataset.target === mediaId;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.gallery.querySelectorAll('[data-n-gallery-slide]').forEach(function (s) {
        s.classList.toggle('is-active', s.dataset.mediaId === mediaId);
      });
    }

    /* ---------------------------------------------------------------- */
    /* Option inputs (size swatch + any extra native selects)            */
    /* ---------------------------------------------------------------- */

    bindOptionInputs() {
      if (this.sizeSwatchWrap) {
        this.sizeSwatchWrap.addEventListener('click', function (e) {
          var btn = e.target.closest('.n-swatch');
          if (!btn) return;
          e.stopPropagation();
          var pos = parseInt(this.sizeSwatchWrap.dataset.optionPosition, 10) - 1;
          this.state.selectedOptions[pos] = btn.dataset.value;
          this.sizeSwatchWrap.querySelectorAll('.n-swatch').forEach(function (s) {
            s.classList.toggle('is-active', s === btn);
          });
          this.onOptionsChanged();
        }.bind(this));
      }

      if (this.nativeOptionsWrap) {
        this.nativeOptionsWrap.addEventListener('change', function (e) {
          var select = e.target.closest('[data-n-option-selector]');
          if (!select) return;
          var pos = parseInt(select.dataset.optionPosition, 10) - 1;
          this.state.selectedOptions[pos] = select.value;
          this.onOptionsChanged();
        }.bind(this));
      }
    }

    onOptionsChanged() {
      this._currentVariant = this.findVariant();
      this.renderFrequencySwatches();
      this.updateAll();
    }

    /* ---------------------------------------------------------------- */
    /* Plan tabs (subscription vs one-time)                              */
    /* ---------------------------------------------------------------- */

    bindPlanTabs() {
      this.planTabs.forEach(function (tab) {
        var head = tab.querySelector('[data-n-plan-select]');
        if (!head) return;
        head.addEventListener('click', function () {
          this.state.plan = head.dataset.nPlanSelect;
          this.reflectPlanSelection();
          this.updateAll();
        }.bind(this));
      }.bind(this));
      this.reflectPlanSelection();
    }

    reflectPlanSelection() {
      this.planTabs.forEach(function (tab) {
        var isSelected = tab.dataset.nPlanTab === this.state.plan;
        tab.classList.toggle('is-selected', isSelected);
        var dot = tab.querySelector('[data-n-plan-dot]');
        if (dot) dot.classList.toggle('is-selected', isSelected);
      }.bind(this));
    }

    /* ---------------------------------------------------------------- */
    /* Delivery frequency (selling plans within the primary group)       */
    /* ---------------------------------------------------------------- */

    renderFrequencySwatches() {
      if (!this.frequencyWrap || !this.primaryGroup) return;
      var variant = this.currentVariant();
      var allocations = (variant && variant.selling_plan_allocations) || [];
      var plans = this.primaryGroup.selling_plans || [];

      this.frequencyWrap.innerHTML = plans.map(function (plan) {
        var alloc = allocations.find(function (a) { return a.selling_plan_id === plan.id; });
        var available = !!alloc;
        return '<button type="button" class="n-swatch' +
          (plan.id === this.state.sellingPlanId ? ' is-active' : '') +
          '" data-plan-id="' + plan.id + '"' +
          (available ? '' : ' disabled') + '>' + plan.name + '</button>';
      }.bind(this)).join('');
    }

    bindFrequency() {
      if (!this.frequencyWrap) return;
      this.frequencyWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('.n-swatch');
        if (!btn || btn.disabled) return;
        e.stopPropagation();
        this.state.sellingPlanId = btn.dataset.planId;
        this.frequencyWrap.querySelectorAll('.n-swatch').forEach(function (s) { s.classList.toggle('is-active', s === btn); });
        this.updateAll();
      }.bind(this));
    }

    /* ---------------------------------------------------------------- */
    /* Quantity tiers (one-time tab)                                     */
    /* ---------------------------------------------------------------- */

    bindQuantityTiers() {
      if (!this.qtyTiersWrap) return;
      this.qtyTiersWrap.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-n-qty-option]');
        if (!btn) return;
        e.stopPropagation();
        this.state.qty = parseInt(btn.dataset.qty, 10) || 1;
        this.state.qtyUnitPriceCents = parseInt(btn.dataset.unitPrice, 10) || null;
        this.qtyTiersWrap.querySelectorAll('[data-n-qty-option]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        this.updateAll();
      }.bind(this));

      var active = this.qtyTiersWrap.querySelector('.is-active');
      if (active) {
        this.state.qty = parseInt(active.dataset.qty, 10) || 1;
        this.state.qtyUnitPriceCents = parseInt(active.dataset.unitPrice, 10) || null;
      }
    }

    /* ---------------------------------------------------------------- */
    /* Rendering                                                         */
    /* ---------------------------------------------------------------- */

    updateAll() {
      var variant = this.currentVariant();
      if (!variant) return;

      this.updatePriceDisplays(variant);
      this.updateRecommendation(variant);
      this.updateCta(variant);
      this.toggleAvailability(variant);

      if (variant.featured_media) this.setActiveMedia(String(variant.featured_media.id));
    }

    getSubscriptionAllocation(variant) {
      var allocations = (variant && variant.selling_plan_allocations) || [];
      return allocations.find(function (a) { return a.selling_plan_id === this.state.sellingPlanId; }.bind(this)) || allocations[0];
    }

    updatePriceDisplays(variant) {
      var fmt = this.moneyFormat;

      if (this.primaryGroup) {
        var alloc = this.getSubscriptionAllocation(variant);
        var subNow = this.querySelector('[data-n-price-subscription]');
        var subWas = this.querySelector('[data-n-compare-subscription]');
        var subUnit = this.querySelector('[data-n-unit-subscription]');
        if (alloc && subNow) {
          subNow.textContent = formatMoney(alloc.price, fmt);
          if (subWas) {
            var compare = alloc.compare_at_price && alloc.compare_at_price > alloc.price ? alloc.compare_at_price : variant.compare_at_price;
            subWas.textContent = compare && compare > alloc.price ? formatMoney(compare, fmt) : '';
          }
          if (subUnit && variant.unit_price) {
            subUnit.textContent = '(' + formatMoney(variant.unit_price, fmt) + '/' + (variant.unit_price_measurement ? variant.unit_price_measurement.reference_value + ' ' + variant.unit_price_measurement.reference_unit : '1 l') + ') ' + (this.dataset.vatLabel || 'inkl. MwSt.');
          }
        }

        var perksPrices = this.querySelectorAll('.n-perks-table [data-n-price-subscription]');
        perksPrices.forEach(function (n) { if (alloc) n.textContent = formatMoney(alloc.price, fmt); });

        var discountBadge = this.querySelector('[data-n-discount-badge]');
        if (discountBadge && alloc && alloc.compare_at_price > alloc.price) {
          var pct = Math.round((1 - alloc.price / alloc.compare_at_price) * 100);
          if (!discountBadge.dataset.staticLabel) discountBadge.textContent = pct + '% ' + (this.dataset.discountSuffix || 'Rabatt');
        }
      }

      var compactPrice = this.querySelector('[data-n-price-onetime-compact]');
      if (compactPrice) compactPrice.textContent = formatMoney(variant.price, fmt);

      var unitOneTime = this.querySelector('[data-n-unit-onetime]');
      if (unitOneTime && variant.unit_price) {
        unitOneTime.textContent = '(' + formatMoney(variant.unit_price, fmt) + '/1 l) ' + (this.dataset.vatLabel || 'inkl. MwSt.');
      }
    }

    updateRecommendation(variant) {
      if (!this.recommendationEl || !this.sizeSwatchWrap) return;
      var pos = parseInt(this.sizeSwatchWrap.dataset.optionPosition, 10) - 1;
      var selectedValue = this.state.selectedOptions[pos];
      var match = this.sizeRecommendations.find(function (r) { return r.matches === selectedValue; });
      if (match) {
        var template = this.recommendationEl.dataset.template || 'Unsere Empfehlung: {dose} · {plan}';
        this.recommendationEl.textContent = template.replace('{dose}', match.dose).replace('{plan}', match.plan);
        this.recommendationEl.hidden = false;
      } else {
        this.recommendationEl.hidden = true;
      }
    }

    updateCta(variant) {
      var ctaPrice = this.querySelector('[data-n-cta-price]');
      var ctaCompare = this.querySelector('[data-n-cta-compare]');
      if (!ctaPrice) return;

      if (this.state.plan === 'subscription' && this.primaryGroup) {
        var alloc = this.getSubscriptionAllocation(variant);
        if (alloc) {
          ctaPrice.textContent = formatMoney(alloc.price, this.moneyFormat);
          if (ctaCompare) {
            var compare = variant.compare_at_price > alloc.price ? variant.compare_at_price : null;
            ctaCompare.textContent = compare ? formatMoney(compare, this.moneyFormat) : '';
          }
        }
      } else {
        var qty = this.state.qty || 1;
        var unit = this.state.qtyUnitPriceCents || variant.price;
        ctaPrice.textContent = formatMoney(unit * qty, this.moneyFormat);
        if (ctaCompare) {
          ctaCompare.textContent = variant.compare_at_price ? formatMoney(variant.compare_at_price * qty, this.moneyFormat) : '';
        }
      }
    }

    toggleAvailability(variant) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = !variant.available;
      var label = this.submitBtn.querySelector('.n-btn__label');
      if (label) label.textContent = variant.available
        ? (this._addToCartLabel || label.textContent)
        : (this.dataset.soldOutLabel || 'Ausverkauft');
      if (!this._addToCartLabel && variant.available && label) this._addToCartLabel = label.textContent;
    }

    /* ---------------------------------------------------------------- */
    /* Add to cart                                                       */
    /* ---------------------------------------------------------------- */

    bindSubmit() {
      var form = this.querySelector('form') || this;
      this.addEventListener('submit', this.onSubmit.bind(this));
      if (this.submitBtn && this.submitBtn.tagName === 'BUTTON' && this.submitBtn.type !== 'submit') {
        this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      }
    }

    onSubmit(e) {
      e.preventDefault();
      var variant = this.currentVariant();
      if (!variant || !variant.available) return;

      var qty = this.state.plan === 'onetime' ? (this.state.qty || 1) : 1;
      var item = { id: variant.id, quantity: qty };
      if (this.state.plan === 'subscription' && this.state.sellingPlanId) {
        item.selling_plan = this.state.sellingPlanId;
      }

      this.setLoading(true);
      this.showError(null);

      fetch(this.dataset.cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] })
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          this.setLoading(false);
          if (!result.ok) {
            this.showError(result.data.description || result.data.message || 'Der Artikel konnte nicht hinzugefügt werden.');
            return;
          }
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { item: result.data } }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        }.bind(this))
        .catch(function () {
          this.setLoading(false);
          this.showError('Netzwerkfehler – bitte versuche es erneut.');
        }.bind(this));
    }

    setLoading(isLoading) {
      if (!this.submitBtn) return;
      this.submitBtn.classList.toggle('n-btn--loading', isLoading);
      this.submitBtn.disabled = isLoading;
    }

    showError(message) {
      if (!this.errorEl) return;
      if (!message) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        return;
      }
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    /* ---------------------------------------------------------------- */
    /* Sticky add-to-cart bar sync                                       */
    /* ---------------------------------------------------------------- */

    initStickyObserver() {
      var sticky = document.querySelector('[data-n-sticky-atc]');
      if (!sticky) return;
      var threshold = parseInt(sticky.dataset.showAfter, 10) || 1200;
      var onScroll = function () {
        var show = window.scrollY > threshold;
        sticky.classList.toggle('is-visible', show);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      // Mirror this form's live price into the sticky bar.
      this._stickyPriceEl = sticky.querySelector('[data-n-cta-price]');
      this._stickyCompareEl = sticky.querySelector('[data-n-cta-compare]');
      var syncSticky = function () {
        var mine = this.querySelector('[data-n-cta-price]');
        var mineCompare = this.querySelector('[data-n-cta-compare]');
        if (this._stickyPriceEl && mine) this._stickyPriceEl.textContent = mine.textContent;
        if (this._stickyCompareEl && mineCompare) this._stickyCompareEl.textContent = mineCompare.textContent;
      }.bind(this);
      var observer = new MutationObserver(syncSticky);
      var target = this.querySelector('[data-n-cta-price]');
      if (target) observer.observe(target, { childList: true, characterData: true, subtree: true });
      syncSticky();

      sticky.addEventListener('click', function (e) {
        if (e.target.closest('[data-n-scroll-to-buy]')) {
          e.preventDefault();
          var section = document.getElementById('kaufen');
          if (section) window.scrollTo({ top: section.getBoundingClientRect().top + window.scrollY - 90, behavior: 'smooth' });
        }
      });
    }
  }

  customElements.define('product-form', NorduraProductForm);
})();
